import { PinOutlined } from "@mui/icons-material";
import { Box, Button, CircularProgress, Link, Stack } from "@mui/material";
import { TextInput } from "components";
import { ENDPOINTS } from "config/constants";
import { useFetch, useTitle, useTranslation } from "hooks";
import { PublicLayout } from "layouts";
import AuthPageLayout from "pages/auth/AuthPageLayout";
import { authFooterLinkSx, authStackSpacing, fieldSx, iconSx, primaryBtnSx } from "pages/auth/formShared";
import { useSnackbar } from "notistack";
import { FormEventHandler, useCallback, useEffect, useMemo, useState } from "react";
import { Link as LinkRouter, useNavigate, useSearchParams } from "react-router-dom";

const defaultValues = { email: "", otp: "" };

interface Data {
  errors?: typeof defaultValues;
}

export default function OTP() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, isSetup] = useMemo(() => {
    return [searchParams.get("email"), searchParams.get("setup") === "true"];
  }, [searchParams]);
  const [state, setState] = useState({
    otp: "",
    email: email || ""
  });
  const [errors, setErrors] = useState({
    otp: "",
    email: email ? "" : "Invalid Email"
  });
  const { enqueueSnackbar } = useSnackbar();
  const { isLoading, response, error, callFetch: callFetchPost } = useFetch<Data>("POST");
  const t = useTranslation();

  const onSubmit: FormEventHandler<HTMLFormElement> = useCallback(
    (e) => {
      e.preventDefault();
      callFetchPost(ENDPOINTS.otp, {
        body: JSON.stringify({
          email: state.email,
          otp: state.otp
        })
      }).catch(console.error);
    },
    [state, callFetchPost]
  );

  useEffect(() => {
    if (response) {
      enqueueSnackbar(response?.message || "", { variant: "success" });
      const params = new URLSearchParams();
      params.append("email", state.email);
      if (isSetup) {
        params.append("setup", "true");
      }
      // The code travels in router state, not the URL, so it never lands in history or server request logs.
      navigate({ pathname: "/forgot-password/password", search: params.toString() }, { state: { otp: state.otp } });
    }
  }, [response, enqueueSnackbar, navigate, state, isSetup]);

  useEffect(() => {
    if (error) {
      enqueueSnackbar(error?.message || JSON.stringify(error), { variant: "error" });
    }
  }, [error, enqueueSnackbar]);

  useTitle(isSetup ? "Set Up Password" : "Forgot Password");

  return (
    <PublicLayout>
      <AuthPageLayout
        title={isSetup ? t["Set Up Password"] : t["Forgot Password"]}
        subtitle={isSetup ? t["Enter OTP welcome email"] : t["Enter OTP received on Email."]}
        footer={
          <Link component={LinkRouter} to="/login" sx={authFooterLinkSx}>
            ← {t["Back to sign in"]}
          </Link>
        }
      >
        <Box component="form" onSubmit={onSubmit}>
          <Stack spacing={authStackSpacing}>
            <TextInput
              autoFocus
              required
              fullWidth
              id="otp"
              name="otp"
              type="text"
              inputMode="numeric"
              label={t["OTP"]}
              placeholder="e.g. 5634"
              helperText={errors?.otp}
              error={!!errors?.otp}
              value={state.otp}
              startAdornment={<PinOutlined sx={iconSx} />}
              sx={fieldSx}
              onChange={(e) => {
                setState((prevState) => ({ ...prevState, otp: e.target.value }));
                setErrors((prevState) => ({ ...prevState, otp: "" }));
              }}
            />
            <Button
              type="submit"
              variant="contained"
              disabled={isLoading}
              endIcon={isLoading ? <CircularProgress size={18} /> : undefined}
              fullWidth
              sx={primaryBtnSx}
            >
              {t["Submit"]}
            </Button>
          </Stack>
        </Box>
      </AuthPageLayout>
    </PublicLayout>
  );
}
